import React, { useMemo, useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import type { BaseCustomProps } from './types';

export interface RechartsDataPoint {
  [key: string]: number | string;
}

export interface SeriesConfig {
  dataKey: string;
  color?: string;
  type?: 'monotone' | 'linear' | 'basis' | 'natural';
  label?: string; // Human-readable label for the series
}

export interface RechartsPlotData {
  data: RechartsDataPoint[];
  series: SeriesConfig[];
  chartType?: 'line' | 'bar' | 'pie' | 'scatter';
  [key: string]: unknown; // Add index signature for compatibility
}

export interface RechartsPlotLayout {
  title?: string;
  showLegend?: boolean;
  showGrid?: boolean;
  margin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  xAxisLabel?: string;
  yAxisLabel?: string;
  pieConfig?: {
    innerRadius?: number;
    outerRadius?: number;
  };
  [key: string]: unknown; // Add index signature for compatibility
}

export interface PlotProps extends BaseCustomProps {
  data?: string | RechartsPlotData | Record<string, unknown>;
  layout?: string | RechartsPlotLayout | Record<string, unknown>;
  className?: string;
  id?: string;
}

let plotCounter = 0;

// Extended color palette for more data points
const COLORS = [
  'hsl(var(--highlight))',    // Orange highlight (primary)
  'hsl(var(--teal))',         // Teal (secondary)
  'hsl(var(--moss))',         // Moss green (success)
  'hsl(var(--coral))',        // Coral (warning) 
  'hsl(var(--navy))',         // Navy (dark accent)
  'hsl(280, 70%, 60%)',       // Purple
  'hsl(45, 80%, 60%)',        // Gold/Yellow
  'hsl(330, 70%, 60%)',       // Pink
  'hsl(180, 70%, 50%)',       // Cyan
  'hsl(90, 60%, 50%)',        // Lime green
  'hsl(30, 70%, 50%)',        // Brown/Orange
  'hsl(240, 60%, 65%)'        // Indigo
];

// Softer colors for backgrounds, fills, etc.
const SOFT_COLORS = [
  'hsl(20, 90%, 85%)',        // Soft orange
  'hsl(200, 60%, 85%)',       // Soft teal
  'hsl(150, 40%, 85%)',       // Soft moss
  'hsl(5, 70%, 85%)',         // Soft coral
  'hsl(215, 50%, 85%)',       // Soft navy
  'hsl(280, 60%, 85%)',       // Soft purple
  'hsl(45, 70%, 85%)',        // Soft gold
  'hsl(330, 60%, 85%)',       // Soft pink
  'hsl(180, 60%, 85%)',       // Soft cyan
  'hsl(90, 50%, 85%)',        // Soft lime
  'hsl(30, 60%, 85%)',        // Soft brown
  'hsl(240, 50%, 85%)'        // Soft indigo
];

export const Plot: React.FC<PlotProps> = ({ data, layout, className, id }) => {
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Update dimensions on mount and window resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        // Set height based on width to maintain aspect ratio
        setContainerWidth(width);
        setContainerHeight(Math.min(width, 500)); // Cap height at 500px, adjust as needed
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const plotId = useMemo(() => id || `plot-${++plotCounter}`, [id]);

  const cleanJsonString = (str: string): string => {
    return str
      // Remove extra newlines (keep one for array/object breaks)
      .replace(/\n\s*\n/g, '\n')
      // Remove whitespace at the start of lines
      .replace(/^\s+/gm, '')
      // Remove whitespace at the end of lines
      .replace(/\s+$/gm, '')
      // Normalize spaces between values
      .replace(/\s+/g, ' ')
      // Fix any broken array brackets
      .replace(/\[\s*\n\s*\]/g, '[]')
      // Remove any remaining unnecessary whitespace around brackets and braces
      .replace(/\s*\[\s*/g, '[')
      .replace(/\s*\]\s*/g, ']')
      .replace(/\s*{\s*/g, '{')
      .replace(/\s*}\s*/g, '}')
      // Fix any broken commas
      .replace(/,\s*\n\s*([}\]])/g, '$1')
      // Ensure proper spacing after colons
      .replace(/:\s+/g, ': ');
  };

  const parsedData = useMemo(() => {
    try {
      if (!data) return null;
      if (typeof data === 'string') {
        const cleanData = cleanJsonString(data);
        const parsed = JSON.parse(cleanData) as RechartsPlotData;
        if (!parsed.data || !Array.isArray(parsed.data) || !parsed.series || !Array.isArray(parsed.series)) {
          console.error('Invalid data structure:', parsed);
          return null;
        }
        return parsed;
      }
      // Check if it's a valid RechartsPlotData object
      if ('data' in data && 'series' in data && 
          Array.isArray((data as RechartsPlotData).data) && 
          Array.isArray((data as RechartsPlotData).series)) {
        return data as RechartsPlotData;
      }
      console.error('Invalid data structure:', data);
      return null;
    } catch (error) {
      console.error('Error parsing plot data:', error);
      return null;
    }
  }, [data]);

  const parsedLayout = useMemo(() => {
    try {
      if (!layout) return {};
      if (typeof layout === 'string') {
        const cleanLayout = cleanJsonString(layout);
        return JSON.parse(cleanLayout) as RechartsPlotLayout;
      }
      // If it's already an object, use it as is
      return layout as RechartsPlotLayout;
    } catch (error) {
      console.error('Error parsing plot layout:', error);
      return {};
    }
  }, [layout]);

  // Create shadcn/ui compatible config from series
  const chartConfig = useMemo(() => {
    if (!parsedData?.series) return {};
    return parsedData.series.reduce((acc, series, index) => {
      const key = `${plotId}-${series.dataKey}-${index}`;
      acc[key] = {
        label: series.label || series.dataKey, // Use label if provided, otherwise dataKey
        color: series.color || COLORS[index % COLORS.length]
      };
      return acc;
    }, {} as Record<string, { label: string; color: string }>);
  }, [parsedData?.series, plotId]);


  if (!parsedData?.data || !parsedData?.series) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">
            Invalid or missing plot data
          </p>
        </CardContent>
      </Card>
    );
  }

  const { margin = { top: 20, right: 30, left: 20, bottom: 30 } } = parsedLayout;

  // Custom tooltip for pie charts
  const CustomPieTooltip = ({ active, payload }: TooltipProps<number, string>) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-popover p-2 rounded shadow-lg border">
          <p className="text-sm font-medium">
            {data.name}: {data.value}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom legend component to ensure labels are displayed
  const CustomLegend = () => {
    if (!parsedData) return null;
    
    // Special handling for pie charts
    if (parsedData.chartType === 'pie') {
      return (
        <div className="flex flex-wrap justify-center gap-4 mt-4">
          {parsedData.data.map((dataPoint, index) => (
            <div key={`legend-${index}`} className="flex items-center gap-2">
              <div 
                className="w-3 h-3 rounded-sm" 
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <span className="text-sm text-muted-foreground">
                {dataPoint.label || dataPoint.x || `Item ${index + 1}`}
              </span>
            </div>
          ))}
        </div>
      );
    }
    
    // Regular legend for other chart types
    if (!parsedData.series) return null;
    
    return (
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {parsedData.series.map((series, index) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-sm" 
              style={{ backgroundColor: series.color || COLORS[index % COLORS.length] }}
            />
            <span className="text-sm text-muted-foreground">
              {series.label || series.dataKey}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderChart = () => {
    const chartContent = () => {
      switch (parsedData.chartType) {
        case 'bar':
          return (
            <BarChart data={parsedData.data} margin={margin}>
              {parsedLayout.showGrid !== false && (
                <CartesianGrid 
                  vertical={false} 
                  className="stroke-muted" 
                  strokeDasharray="3 3" 
                  strokeOpacity={0.5}
                />
              )}
              <XAxis
                dataKey="x"
                label={parsedLayout.xAxisLabel ? {
                  value: parsedLayout.xAxisLabel,
                  position: 'bottom'
                } : undefined}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                label={parsedLayout.yAxisLabel ? {
                  value: parsedLayout.yAxisLabel,
                  angle: -90,
                  position: 'insideLeft'
                } : undefined}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground text-xs"
                tick={{ fontSize: 12 }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              {parsedData.series.map((series: SeriesConfig, index: number) => {
                const key = `${plotId}-${series.dataKey}-${index}`;
                return (
                  <Bar
                    key={key}
                    dataKey={series.dataKey}
                    fill={series.color || COLORS[index % COLORS.length]}
                    radius={4}
                    name={series.label || series.dataKey}
                  />
                );
              })}
            </BarChart>
          );
  
        case 'pie':
          return (
            <PieChart margin={margin}>
              {parsedData.series.map((series: SeriesConfig, index: number) => {
                const key = `${plotId}-${series.dataKey}-${index}`;
                return (
                  <Pie
                    key={key}
                    data={parsedData.data}
                    dataKey={series.dataKey}
                    nameKey="x"
                    cx="50%"
                    cy="50%"
                    innerRadius={parsedLayout.pieConfig?.innerRadius || 0}
                    outerRadius={parsedLayout.pieConfig?.outerRadius || '80%'}
                    label={(entry) => `${entry.value}`}
                    name={series.label || series.dataKey}
                  >
                    {parsedData.data.map((_, cellIndex) => (
                      <Cell 
                        key={`${key}-cell-${cellIndex}`} 
                        fill={COLORS[cellIndex % COLORS.length]} 
                      />
                    ))}
                  </Pie>
                );
              })}
              <ChartTooltip content={CustomPieTooltip} />
            </PieChart>
          );
  
        case 'scatter':
          return (
            <ScatterChart margin={margin}>
              {parsedLayout.showGrid !== false && (
                <CartesianGrid 
                  vertical={false} 
                  className="stroke-muted" 
                  strokeDasharray="3 3" 
                  strokeOpacity={0.5}
                />
              )}
              <XAxis
                dataKey="x"
                type="number"
                label={parsedLayout.xAxisLabel ? {
                  value: parsedLayout.xAxisLabel,
                  position: 'bottom'
                } : undefined}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                dataKey="y"
                type="number"
                label={parsedLayout.yAxisLabel ? {
                  value: parsedLayout.yAxisLabel,
                  angle: -90,
                  position: 'insideLeft'
                } : undefined}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground text-xs"
                tick={{ fontSize: 12 }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              {parsedData.series.map((series: SeriesConfig, index: number) => {
                const key = `${plotId}-${series.dataKey}-${index}`;
                return (
                  <Scatter
                    key={key}
                    data={parsedData.data}
                    fill={series.color || COLORS[index % COLORS.length]}
                    name={series.label || series.dataKey}
                  />
                );
              })}
            </ScatterChart>
          );
  
        case 'line':
        default:
          return (
            <LineChart data={parsedData.data} margin={margin}>
              {parsedLayout.showGrid !== false && (
                <CartesianGrid 
                  vertical={false} 
                  className="stroke-muted" 
                  strokeDasharray="3 3" 
                  strokeOpacity={0.5}
                />
              )}
              <XAxis
                dataKey="x"
                label={parsedLayout.xAxisLabel ? {
                  value: parsedLayout.xAxisLabel,
                  position: 'bottom'
                } : undefined}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                label={parsedLayout.yAxisLabel ? {
                  value: parsedLayout.yAxisLabel,
                  angle: -90,
                  position: 'insideLeft'
                } : undefined}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground text-xs"
                tick={{ fontSize: 12 }}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              {parsedData.series.map((series: SeriesConfig, index: number) => {
                const key = `${plotId}-${series.dataKey}-${index}`;
                return (
                  <Line
                    key={key}
                    type={series.type || "monotone"}
                    dataKey={series.dataKey}
                    stroke={series.color || COLORS[index % COLORS.length]}
                    strokeWidth={2}
                    dot={{ fill: series.color || COLORS[index % COLORS.length], r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                    name={series.label || series.dataKey}
                  />
                );
              })}
            </LineChart>
          );
      }
    };

    return (
      <ResponsiveContainer height="100%" aspect={1}>
        {chartContent()}
      </ResponsiveContainer>
    );
  };

  return (
    <Card className={cn("w-full max-w-lg mx-auto shadow-sm", className)} id={plotId}>
      {parsedLayout.title && (
        <CardHeader className="p-2 sm:p-4 pb-0 sm:pb-0">
          <CardTitle className="text-sm sm:text-base">{parsedLayout.title}</CardTitle>
        </CardHeader>
      )}
      <CardContent className="p-2 sm:p-4">
        <div className="w-full">
          <div ref={containerRef} className="w-full" style={{ height: containerHeight }}>
            {containerWidth > 0 && containerHeight > 0 && (
              <ChartContainer config={chartConfig} className="w-full h-full">
                <ResponsiveContainer width="100%" height="100%">
                  {renderChart()}
                </ResponsiveContainer>
              </ChartContainer>
            )}
          </div>
          {parsedLayout.showLegend !== false && <CustomLegend />}
        </div>
      </CardContent>
    </Card>
  );
};

export default Plot;